import { defineStore } from 'pinia'
import api from '@/api'
import config from '@/config/local'
import {useUserStore} from "@/stores/user.js"
import * as Sentry from "@sentry/browser";

interface AuthUser {
	scopes: string[];
	[name: string]: any;
}


export const useAuthStore = defineStore('auth', {
	state: () => ({
		authToken: (() => {
			return new URLSearchParams(window.location.search).get('auth_token') ||
				localStorage.getItem('authToken') ||
				null
		})(),
		authUser: null as AuthUser | null,
		isAuthUserLoaded: false
	}),

	getters: {
		isAuthenticated: (state) => !!state.authToken,
		isUser: (state) => state.authUser && state.authUser.scopes.length === 1 && state.authUser.scopes[0] === 'course',
		isAdmin: (state) => state.authUser && state.authUser.scopes.indexOf('admin') > -1,
		scopes: (state) => state.authUser?.scopes || []
	},

	actions: {
		login(authToken: string) {
			this.authToken = authToken
			localStorage.setItem('authToken', authToken)
			api.defaults.headers.common['Auth-Token'] = authToken
		},

		logout() {
			this.authToken = null
			localStorage.removeItem('authToken')
			delete api.defaults.headers.common['Auth-Token']
		},

		setAuthUser(user: AuthUser) {
			this.authUser = user
		},

		setAuthUserLoading(status: boolean) {
			this.isAuthUserLoaded = status
		},

		async loginAction() {
			if (this.authToken) {
				this.login(this.authToken)
				await this.getAuthenticatedUser()
        const urlParams = new URLSearchParams(window.location.search)
        if(urlParams.has('auth_token')) {
          urlParams.delete('auth_token')
          const newUrl = window.location.origin + window.location.pathname +
            (urlParams.toString() ? '?' + urlParams.toString() : '')
          window.location.replace(newUrl)
        }
        await useUserStore().addEvent({name: 'auth', params: []})
			} else {
				const returnUrl = encodeURIComponent(window.location.href)
				location.href = `${config.apiHost}/auth/login?return_url=${returnUrl}`
			}
		},

		logoutAction() {
      this.logout()
      location.href = `${config.apiHost}/auth/logout`
		},

		async getAuthenticatedUser() {
			try {
				const userStore = useUserStore()
				const user = await userStore.get()
        if(user) {
          this.setAuthUser(user)
          Sentry.setUser({id: user.id})
          this.setAuthUserLoading(true)
        }
				return user
			} catch (error) {
				this.logout()
				this.setAuthUserLoading(false)
        Sentry.captureException(error)
				console.error('Ошибка при получении данных пользователя при авторизации: ', error)
			}
		},

		checkInputAs() {
			return new Promise<void>(resolve => {
				const uri = window.location.search.substr(1)
				const params = new URLSearchParams(uri)
				if (params.has('inputas')) {
					const inputasGet = params.get('inputas')
					if (inputasGet) {
						api.defaults.headers.common.InputAs = inputasGet
						localStorage.setItem('inputAs', inputasGet)
					} else {
						delete api.defaults.headers.common.InputAs
						localStorage.removeItem('inputAs')
					}
				} else {
					const inputas = localStorage.getItem('inputAs')
					if (inputas) {
						api.defaults.headers.common.InputAs = inputas
					}
				}
				resolve()
			})
		},

		clearInputAs() {
			return new Promise<void>(resolve => {
				delete api.defaults.headers.common.InputAs
				localStorage.removeItem('inputAs')
				resolve()
			})
		}
	}
})
